import { FeatureLike } from 'ol/Feature';
import { ArcGISRenderer, ArcGISSymbol, ArcGISUniqueValueInfo } from '../models/arcgis-types';

function valueKey(value: unknown): string {
    return value === null || value === undefined ? '<Null>' : String(value);
}

export function normalizeArcGISRenderer(renderer: ArcGISRenderer): ArcGISRenderer {
    if (renderer.type !== 'uniqueValue' || !renderer.uniqueValueGroups) {
        return renderer;
    }
    const delimiter = renderer.fieldDelimiter ?? ',';
    return {
        ...renderer,
        uniqueValueInfos: renderer.uniqueValueGroups.flatMap(group => group.classes.flatMap(entry =>
            entry.values.map(values => ({
                value: values.map(valueKey).join(delimiter),
                symbol: entry.symbol,
                label: entry.label,
                description: entry.description
            }))
        ))
    };
}

export function getArcGISUniqueValueKey(renderer: ArcGISRenderer, feature: FeatureLike): string {
    if (!renderer.field1 && renderer.valueExpression) {
        return valueKey(feature.get('__arcadeValue'));
    }
    const fields = [renderer.field1 || renderer.field, renderer.field2, renderer.field3]
        .filter((field): field is string => !!field);
    return fields.map(field => valueKey(feature.get(field))).join(renderer.fieldDelimiter ?? ',');
}

export function getArcGISUniqueValueLegendInfos(renderer: ArcGISRenderer): ArcGISUniqueValueInfo[] {
    if (!renderer.uniqueValueGroups) {
        return renderer.uniqueValueInfos || [];
    }
    return renderer.uniqueValueGroups.flatMap(group => group.classes.map(entry => ({
        value: entry.values[0]?.map(valueKey).join(renderer.fieldDelimiter ?? ',') ?? '',
        label: entry.label,
        symbol: entry.symbol,
        description: entry.description
    })));
}

export function selectArcGISSymbol(renderer: ArcGISRenderer, feature: FeatureLike): ArcGISSymbol | undefined {
    if (renderer.type === 'simple') return renderer.symbol;
    if (renderer.type === 'uniqueValue') {
        const key = getArcGISUniqueValueKey(renderer, feature);
        return renderer.uniqueValueInfos?.find(info => String(info.value) === key)?.symbol || renderer.defaultSymbol;
    }
    if (renderer.type === 'classBreaks') {
        const value = Number(feature.get(renderer.field || ''));
        return renderer.classBreakInfos?.find(info =>
            value >= (info.classMinValue ?? renderer.minValue ?? -Infinity) && value <= (info.classMaxValue ?? Infinity)
        )?.symbol || renderer.defaultSymbol;
    }
    return renderer.defaultSymbol || renderer.symbol;
}

export function getArcGISRendererSymbols(renderer: ArcGISRenderer): ArcGISSymbol[] {
    const symbols = [renderer.symbol, renderer.defaultSymbol]
        .filter((symbol): symbol is ArcGISSymbol => !!symbol);
    return symbols.concat(
        (renderer.uniqueValueInfos || []).map(info => info.symbol),
        (renderer.classBreakInfos || []).map(info => info.symbol)
    );
}
